import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { CloseIcon } from './icons.jsx';

export const SIDEBAR_MODAL_MESSAGE_TYPE = 'cst-sidebar-open-modal';
export const SIDEBAR_MODAL_CLOSE_MESSAGE_TYPE = 'cst-sidebar-close-modal';

export function isSidebarModalRequestPayload(data) {
  return Boolean(
    data
    && typeof data === 'object'
    && data.type === SIDEBAR_MODAL_MESSAGE_TYPE
    && typeof data.sourceUrl === 'string'
    && data.sourceUrl.trim() !== ''
    && typeof data.modalId === 'string'
    && data.modalId.trim() !== ''
  );
}

export function buildSidebarModalRequest(payload, sourceWindow) {
  return {
    sequence: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    modalId: payload.modalId || '',
    sourceUrl: payload.sourceUrl || '',
    sourceWindow: sourceWindow || null,
    title: typeof payload.title === 'string' ? payload.title.trim() : '',
    size: typeof payload.size === 'string' ? payload.size.trim() : '',
  };
}

export function findKnownSidebarFrame(sourceWindow) {
  if (!sourceWindow || typeof document === 'undefined') {
    return null;
  }

  const frames = document.querySelectorAll('.cat-react-sidebar-fallback__frame[data-cst-sidebar-modal-source="1"]');
  return Array.from(frames).find((frame) => {
    if (!(frame instanceof HTMLIFrameElement)) {
      return false;
    }

    try {
      return frame.contentWindow === sourceWindow;
    } catch (error) {
      return false;
    }
  }) || null;
}

export function resolveSidebarModalSize(request) {
  switch (request?.size) {
    case 'sm':
      return 'is-sm';
    case 'xl':
      return 'is-xl';
    case 'full':
      return 'is-full';
    default:
      return 'is-lg';
  }
}

export function SidebarIframeModalBridge({ request, onClose }) {
  const shouldReduceMotion = useReducedMotion();
  const mountRef = useRef(null);
  const restoreRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    document.body.classList.add('cst-sidebar-parent-modal-open');

    return () => {
      document.body.classList.remove('cst-sidebar-parent-modal-open');
    };
  }, []);

  useLayoutEffect(() => {
    const sourceWindow = request?.sourceWindow;
    if (!sourceWindow || !mountRef.current) {
      return undefined;
    }

    const frame = findKnownSidebarFrame(sourceWindow);
    if (!(frame instanceof HTMLIFrameElement)) {
      onClose();
      return undefined;
    }

    const shell = frame.closest('[data-cst-sidebar-fallback-shell="1"]');
    if (!(shell instanceof HTMLElement)) {
      onClose();
      return undefined;
    }

    const originalShellStyle = shell.getAttribute('style');
    const originalFrameStyle = frame.getAttribute('style');
    const originalParent = shell.parentNode;
    const originalNextSibling = shell.nextSibling;
    restoreRef.current = {
      shell,
      frame,
      originalParent,
      originalNextSibling,
      originalShellStyle,
      originalFrameStyle,
      sourceWindow,
    };

    mountRef.current.appendChild(shell);

    try {
      sourceWindow.__cstSidebarParentModalActive = true;
      sourceWindow.CstAdminToolsSidebarModalController?.open(request.modalId, {
        forceLocal: true,
        trigger: null,
      });
    } catch (error) {
      console.warn('[cst_admin_tools] unable to open lifted sidebar modal', error);
    }
    shell.style.position = 'absolute';
    shell.style.inset = '0';
    shell.style.width = '100%';
    shell.style.height = '100%';
    shell.style.minHeight = '0';
    shell.style.background = 'transparent';

    frame.style.position = 'absolute';
    frame.style.inset = '0';
    frame.style.width = '100%';
    frame.style.height = '100%';
    frame.style.minHeight = '0';
    frame.style.borderRadius = '0';
    frame.style.background = 'transparent';

    const readyHandle = window.requestAnimationFrame(() => {
      setIsReady(true);
    });

    return () => {
      setIsReady(false);
      window.cancelAnimationFrame(readyHandle);
      const restoreState = restoreRef.current;
      restoreRef.current = null;

      if (!restoreState) {
        return;
      }

      try {
        restoreState.sourceWindow.__cstSidebarParentModalActive = false;
      } catch (error) {
        // Ignore cross-window cleanup failures.
      }

      if (restoreState.originalShellStyle === null) {
        restoreState.shell.removeAttribute('style');
      } else {
        restoreState.shell.setAttribute('style', restoreState.originalShellStyle);
      }

      if (restoreState.originalFrameStyle === null) {
        restoreState.frame.removeAttribute('style');
      } else {
        restoreState.frame.setAttribute('style', restoreState.originalFrameStyle);
      }

      if (!(restoreState.originalParent instanceof Node)) {
        return;
      }

      if (restoreState.originalNextSibling instanceof Node && restoreState.originalNextSibling.parentNode === restoreState.originalParent) {
        restoreState.originalParent.insertBefore(restoreState.shell, restoreState.originalNextSibling);
        return;
      }

      restoreState.originalParent.appendChild(restoreState.shell);
    };
  }, [onClose, request]);

  function handleRequestClose() {
    const sourceWindow = request?.sourceWindow;
    if (sourceWindow?.CstAdminToolsSidebarModalController?.closeAll) {
      const handled = sourceWindow.CstAdminToolsSidebarModalController.closeAll();
      if (handled) {
        return;
      }
    }

    onClose();
  }

  const overlayTransition = shouldReduceMotion
    ? { duration: 0 }
    : { duration: 0.18, ease: [0.22, 1, 0.36, 1] };
  const panelTransition = shouldReduceMotion
    ? { duration: 0 }
    : { type: 'spring', stiffness: 360, damping: 34, mass: 0.9 };

  return (
    <div className="cat-react-global-modal-layer">
      <motion.button
        type="button"
        className="cat-react-global-modal__backdrop"
        aria-label="Fermer la modale"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={overlayTransition}
        onClick={handleRequestClose}
      />
      <motion.div
        className={`cat-react-global-modal__frame-shell ${resolveSidebarModalSize(request)}`.trim()}
        initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.985 }}
        transition={panelTransition}
      >
        <button
          type="button"
          className="cat-react-global-modal__close"
          onClick={handleRequestClose}
          aria-label="Fermer"
          title="Fermer"
        >
          <CloseIcon />
          <span className="cat-react-visually-hidden">Fermer</span>
        </button>
        <div ref={mountRef} className="cat-react-global-modal__mount">
          {!isReady ? <div className="cat-react-state">Chargement de la modale…</div> : null}
        </div>
      </motion.div>
    </div>
  );
}
